import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/rentApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Home, Building2, Wrench, UserSearch, KeyRound } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  ["proprietaire", "Propriétaire", "Je possède des biens à gérer", Building2],
  ["candidat", "Candidat", "Je cherche un logement", UserSearch],
  ["locataire", "Locataire", "Je loue déjà un logement", KeyRound],
  ["mainteneur", "Mainteneur", "J'interviens sur les biens", Wrench],
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "proprietaire", consent: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Compte créé");
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground grid place-items-center"><Home className="w-4 h-4" /></div>
          <span className="font-heading font-bold text-xl">Rent</span>
        </Link>
        <div className="border border-border rounded-2xl bg-card p-6 lg:p-8">
          <h1 className="font-heading text-2xl font-bold">Créer un compte</h1>
          <p className="text-sm text-muted-foreground mt-1">Choisissez votre profil pour commencer.</p>

          <div className="grid grid-cols-2 gap-2 mt-5">
            {ROLES.map(([value, label, desc, Icon]) => (
              <button
                key={value}
                type="button"
                onClick={() => setForm({ ...form, role: value })}
                data-testid={`role-option-${value}`}
                className={`text-left p-3 rounded-xl border transition-colors ${
                  form.role === value ? "border-primary bg-accent" : "border-border hover:border-primary/40"
                }`}
              >
                <Icon className="w-4 h-4 mb-1.5 text-[hsl(var(--ring))]" />
                <p className="text-sm font-semibold">{label}</p>
                <p className="text-[11px] text-muted-foreground leading-tight">{desc}</p>
              </button>
            ))}
          </div>

          {form.role === "proprietaire" && (
            <p className="mt-3 text-xs text-muted-foreground bg-accent/60 rounded-lg p-2.5">
              Abonnement propriétaire : <strong>150 €/an par adresse</strong> (plusieurs lots possibles dans un même
              immeuble), tout compris.
            </p>
          )}

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom complet</Label>
              <Input id="name" value={form.name} onChange={set("name")} required data-testid="register-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="register-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone</Label>
                <Input id="phone" value={form.phone} onChange={set("phone")} data-testid="register-phone" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="register-password" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
            <div className="flex items-start gap-2">
              <Checkbox id="consent" checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} data-testid="register-consent" className="mt-0.5" />
              <Label htmlFor="consent" className="text-xs text-muted-foreground leading-snug font-normal">
                J'accepte que mes données soient traitées conformément à la{" "}
                <a href="/confidentialite" target="_blank" rel="noreferrer" className="underline text-foreground">politique de confidentialité</a> (RGPD).
              </Label>
            </div>
            <Button type="submit" className="w-full rounded-full" disabled={loading || !form.consent} data-testid="register-submit">
              {loading ? "Création…" : "Créer mon compte"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-5 text-center">
            Déjà inscrit ? <Link to="/login" className="text-foreground font-medium underline">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
